/**
 * ImageContent - 图片内容渲染组件
 */

import React, { useEffect, useMemo, useState } from 'react';
import { getProxyUrl } from '../../utils/urlProxy';
import { LazyImage } from '../common/LazyMedia';

export default function ImageContent({ data, mode = 'view', templateType, priority = false }) {
  // Hooks must run unconditionally
  const hasData = !!data;
  const hasImages = hasData && data.images && data.images.length > 0;
  const hasBackgroundColor = hasData && data.backgroundColor && data.backgroundColor !== '#ffffff';
  const isStandardTemplate = templateType === 'standard-food-label';
  const imgStyle = hasData ? (data.imageStyle || {}) : {};
  const isBlueWhiteTemplate = templateType === 'blue-white-template';
  const carousel = hasData ? (data.carousel || {}) : {};
  const titleConfig = hasData ? (data.titleConfig || {}) : {};
  const shouldShowCustomTitle = titleConfig.enabled && (titleConfig.chinese || titleConfig.english);

  const normalizedImages = useMemo(() => {
    if (!hasImages || !data) return [];
    return data.images.map((img) => ({
      src: typeof img === 'string' ? img : img.src,
      width: typeof img === 'string' ? 100 : (img.width || 100)
    }));
  }, [data, hasImages]);

  const [activeIndex, setActiveIndex] = useState(0);

  // 自动播放（在 hook 内部做条件判断，确保 hook 顺序固定）
  useEffect(() => {
    if (!carousel.enabled || !carousel.autoplay || normalizedImages.length <= 1) {
      return undefined;
    }
    const interval = carousel.interval || 3000;
    const timer = setInterval(() => {
      setActiveIndex((prev) => {
        const next = prev + 1;
        if (next >= normalizedImages.length) {
          return carousel.loop === false ? prev : 0;
        }
        return next;
      });
    }, interval);
    return () => clearInterval(timer);
  }, [carousel.enabled, carousel.autoplay, carousel.interval, carousel.loop, normalizedImages.length]);

  const goTo = (index) => {
    if (!carousel.enabled || normalizedImages.length <= 1) return;
    const total = normalizedImages.length;
    let next = index;
    if (next < 0) next = carousel.loop === false ? 0 : total - 1;
    if (next >= total) next = carousel.loop === false ? total - 1 : 0;
    setActiveIndex(next);
  };

  if (!hasData) return null;

  const renderTitle = () => {
    const bg = titleConfig.bgColor || '#1e90ff';
    const tc = titleConfig.textColor || '#ffffff';

    if (isBlueWhiteTemplate && shouldShowCustomTitle) {
      return (
        <h3
          className="content-section-heading blue-white-title"
          style={{
            backgroundColor: bg,
            color: tc
          }}
        >
          {titleConfig.english && (
            <div className="blue-white-title-en">{titleConfig.english}</div>
          )}
          <div className="blue-white-title-cn">{titleConfig.chinese || '产品图'}</div>
        </h3>
      );
    }

    if (isBlueWhiteTemplate) {
      return <h3 className="content-section-heading">产品图</h3>;
    }

    if (shouldShowCustomTitle) {
      return (
        <h3
          className="image-section-title"
          style={{
            backgroundColor: bg,
            color: tc,
            padding: '10px 12px',
            borderRadius: '6px',
            marginBottom: '12px'
          }}
        >
          {titleConfig.english && <div style={{ fontSize: 12, opacity: 0.9, lineHeight: 1.2 }}>{titleConfig.english}</div>}
          <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>{titleConfig.chinese || '产品图'}</div>
        </h3>
      );
    }

    if (isStandardTemplate) {
      return (
        <h3
          className="image-section-title"
          style={{
            backgroundColor: '#1e90ff',
            color: '#ffffff',
            padding: '10px 12px',
            borderRadius: '6px',
            marginBottom: '12px'
          }}
        >
          <div style={{ fontSize: 12, opacity: 0.9, lineHeight: 1.2 }}>Product Images.</div>
          <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>产品图</div>
        </h3>
      );
    }

    return null;
  };

  // 蓝白模板：标题放置在白卡片外，显示在蓝色背景上
  if (isBlueWhiteTemplate) {
    return (
      <>
        {renderTitle()}
        <div
          className="label-content-images"
          style={{ backgroundColor: data.backgroundColor || 'transparent' }}
        >
          {hasImages ? (
            carousel.enabled && normalizedImages.length > 1 ? (
              <div className="image-carousel">
                <div
                  className="carousel-track"
                  style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                >
                  {normalizedImages.map((img, idx) => {
                    const proxyImgSrc = getProxyUrl(img.src);
                    return (
                      <div className="carousel-slide" key={idx}>
                        <LazyImage
                          src={proxyImgSrc}
                          alt={`图片 ${idx + 1}`}
                          priority={priority || idx === 0}
                          style={{
                            borderRadius: imgStyle.radius ? imgStyle.radius + 'px' : undefined,
                            border: imgStyle.borderWidth ? `${imgStyle.borderWidth}px solid ${imgStyle.borderColor || '#e8eaed'}` : undefined
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
                {carousel.showArrows !== false && (
                  <>
                    <button className="carousel-arrow prev" onClick={() => goTo(activeIndex - 1)} aria-label="上一张">‹</button>
                    <button className="carousel-arrow next" onClick={() => goTo(activeIndex + 1)} aria-label="下一张">›</button>
                  </>
                )}
                {carousel.showDots !== false && (
                  <div className="carousel-dots">
                    {normalizedImages.map((_, idx) => (
                      <button
                        key={idx}
                        className={`carousel-dot ${idx === activeIndex ? 'active' : ''}`}
                        onClick={() => setActiveIndex(idx)}
                        aria-label={`跳转到第${idx + 1}张`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              normalizedImages.map((img, idx) => {
                const proxyImgSrc = getProxyUrl(img.src);
                return (
                  <div
                    key={idx}
                    className="image-wrapper"
                    style={{ width: `${img.width}%` }}
                  >
                    <LazyImage
                      src={proxyImgSrc}
                      alt={`图片 ${idx + 1}`}
                      priority={priority || idx === 0}
                      style={{
                        borderRadius: imgStyle.radius ? imgStyle.radius + 'px' : undefined,
                        border: imgStyle.borderWidth ? `${imgStyle.borderWidth}px solid ${imgStyle.borderColor || '#e8eaed'}` : undefined
                      }}
                    />
                  </div>
                );
              })
            )
          ) : (
            <div className="image-placeholder">
              {hasBackgroundColor ? '背景色预览' : '暂无图片'}
            </div>
          )}
          {data.description && (
            <div className="image-description">{data.description}</div>
          )}
        </div>
      </>
    );
  }

  return (
    <div
      className="label-content-images"
      style={{ backgroundColor: data.backgroundColor || 'transparent' }}
    >
      {renderTitle()}
      {hasImages ? (
        carousel.enabled && normalizedImages.length > 1 ? (
          <div className="image-carousel">
            <div
              className="carousel-track"
              style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            >
              {normalizedImages.map((img, idx) => {
                const proxyImgSrc = getProxyUrl(img.src);
                return (
                  <div className="carousel-slide" key={idx}>
                    <LazyImage
                      src={proxyImgSrc}
                      alt={`图片 ${idx + 1}`}
                      priority={priority || idx === 0}
                      style={{
                        borderRadius: imgStyle.radius ? imgStyle.radius + 'px' : undefined,
                        border: imgStyle.borderWidth ? `${imgStyle.borderWidth}px solid ${imgStyle.borderColor || '#e8eaed'}` : undefined
                      }}
                    />
                  </div>
                );
              })}
            </div>
            {carousel.showArrows !== false && (
              <>
                <button className="carousel-arrow prev" onClick={() => goTo(activeIndex - 1)} aria-label="上一张">‹</button>
                <button className="carousel-arrow next" onClick={() => goTo(activeIndex + 1)} aria-label="下一张">›</button>
              </>
            )}
            {carousel.showDots !== false && (
              <div className="carousel-dots">
                {normalizedImages.map((_, idx) => (
                  <button
                    key={idx}
                    className={`carousel-dot ${idx === activeIndex ? 'active' : ''}`}
                    onClick={() => setActiveIndex(idx)}
                    aria-label={`跳转到第${idx + 1}张`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          normalizedImages.map((img, idx) => {
            const proxyImgSrc = getProxyUrl(img.src);
            return (
              <div
                key={idx}
                className="image-wrapper"
                style={{ width: `${img.width}%` }}
              >
                <LazyImage
                  src={proxyImgSrc}
                  alt={`图片 ${idx + 1}`}
                  priority={priority || idx === 0} // 第一张图片高优先级
                  style={{
                    borderRadius: imgStyle.radius ? imgStyle.radius + 'px' : undefined,
                    border: imgStyle.borderWidth ? `${imgStyle.borderWidth}px solid ${imgStyle.borderColor || '#e8eaed'}` : undefined
                  }}
                />
              </div>
            );
          })
        )
      ) : (
        <div className="image-placeholder">
          {hasBackgroundColor ? '背景色预览' : '暂无图片'}
        </div>
      )}
      {data.description && (
        <div className="image-description">{data.description}</div>
      )}
    </div>
  );
}
